import React from 'react';
import {
  LoginAccountState,
  useLoginAccount,
} from 'src/globalStates/loginAccount';
import { MCustomer, MCustomerId } from 'src/models/mCustomer';
import { MCustomerRepository } from 'src/repositories/mCustomer/interface';
import { useMCustomerRepository } from 'src/repositories/mCustomer/repository';
import { toDownloadDataFormat } from 'src/repositories/mCustomer/converter';
import {
  convertFirstAttribute,
  convertSecondAttribute,
  isHikariFood,
} from 'src/components/function/customMerchant';
import { download } from 'src/util/download';
import { message } from 'antd';
import { useMCustomerCacheMutator } from './cache';
import { useSWRConfig } from 'swr';

// CSVアップロード用のデータ型
export interface CSVUpdateData {
  lineId: string;
  nickname: string;
  remarks: string;
}

// CSVアップロードの結果型
export interface BulkUpdateResult {
  success: number;
  failed: number;
  errors: string[];
  updatedMCustomerIds: string[];
}

export const useMCustomerUsecase = () => {
  const repository = useMCustomerRepository();
  const account = useLoginAccount();
  const mutator = useMCustomerCacheMutator();
  const { mutate: globalMutate } = useSWRConfig();

  return React.useMemo(
    () => createMCustomerUsecase({ repository, account, mutator, globalMutate }),
    [repository, account, mutator, globalMutate]
  );
};

export interface MCustomerUsecase {
  updateMCustomer(value: MCustomer): Promise<MCustomer>;
  downloadAllMCustomers(format: 'csv' | 'xlsx'): Promise<void>;
  account?: LoginAccountState;
}

const createMCustomerUsecase = ({
  repository,
  account,
  mutator,
  globalMutate
}: {
  repository: MCustomerRepository;
  account: LoginAccountState;
  mutator: ReturnType<typeof useMCustomerCacheMutator>;
  globalMutate: ReturnType<typeof useSWRConfig>['mutate'];
}) => ({
  hikariFoodState() {
    return {
      isAccount: isHikariFood(account.merchantId!),
      firstAttribute: convertFirstAttribute(account.merchantId!),
      secondAttribute: convertSecondAttribute(account.merchantId!),
    };
  },

  async updateMCustomer(value: MCustomer) {
    try {
      const _newCustomers = {
        ...value,
        firstAttribute:
          value.firstAttribute === 'null' ? null : value.firstAttribute,
        secondAttribute:
          value.secondAttribute === 'null' ? null : value.secondAttribute,
      };

      await repository.updateDoc({
        mCustomer: _newCustomers,
      });

      message.success('保存しました');

      mutator.mutateItem({ mCustomerId: value.id });
    } catch (error) {
      message.error('予期せぬエラーが発生しました。再度お試しください。');
    }
  },

  async downloadAllMCustomers(format: 'csv' | 'xlsx') {
    try {
      const _results = await repository.getAllDocs(account);
      const _data = toDownloadDataFormat(_results, account);
      download(_data, '顧客データ', format);
    } catch (error) {
      message.error(
        '予期せぬエラーが発生しました。ページを更新して再度お試しください。'
      );
    }
  },

  // MCustomerのニックネーム、コメント内容更新
  async bulkUpdateCustomers(csvData: CSVUpdateData[]): Promise<BulkUpdateResult> {
    let updateResult: BulkUpdateResult = {
      success: 0,
      failed: 0,
      errors: [],
      updatedMCustomerIds: [],
    };

    try {
      // CSVデータ一括更新処理
      updateResult = await repository.bulkUpdateByLineIds(csvData);

      // キャッシュ更新: 更新された顧客データのキャッシュを個別に無効化
      if (updateResult.updatedMCustomerIds.length > 0) {
        updateResult.updatedMCustomerIds.forEach(mCustomerId => {
          mutator.mutateItem({ mCustomerId: mCustomerId as MCustomerId });
        });

        globalMutate(
          (key) => Array.isArray(key) && (
            key[0] === 'MCUSTOMER' ||    // 顧客データキャッシュ
            key[0] === 'MESSAGEROOM'     // DM関連キャッシュ
          ),
          undefined,
          { revalidate: true }
        );
      }

      return updateResult;
    }
    catch (error) {
      updateResult.failed = csvData.length;
      updateResult.errors.push('一括更新処理中に予期せぬエラーが発生しました');
      throw error; // エラーを再throw
    }
  }
});
